import { SearchParams } from '../../types'
import type { CrocoTransactionFilters } from '../../types/croco'

// React Query keys for caching
export const queryKeys = {
  callCampaigns: {
    all: ['callCampaigns'] as const,
    list: (filters?: Record<string, any>) =>
      ['callCampaigns', 'list', filters ?? {}] as const,
    detail: (id: number) => ['callCampaigns', 'detail', id] as const,
    stats: (id: number) => ['callCampaigns', 'stats', id] as const,
    calls: (campaignId: number, filters?: Record<string, any>) =>
      ['callCampaigns', 'calls', campaignId, filters ?? {}] as const,
    call: (campaignId: number, callId: number) =>
      ['callCampaigns', 'call', campaignId, callId] as const,
    followUps: () => ['callCampaigns', 'followUps'] as const,
    config: {
      elevenLabs: () => ['callCampaigns', 'config', 'elevenLabs'] as const,
      twilio: () => ['callCampaigns', 'config', 'twilio'] as const,
      bots: () => ['callCampaigns', 'config', 'bots'] as const,
      phoneNumbers: () => ['callCampaigns', 'config', 'phoneNumbers'] as const,
    },
  },
  announcements: {
    all: ['announcements'] as const,
    lists: () => [...queryKeys.announcements.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.announcements.lists(), filters] as const,
    details: () => [...queryKeys.announcements.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.announcements.details(), id] as const,
  },
  news: {
    all: ['news'] as const,
    lists: () => [...queryKeys.news.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.news.lists(), filters] as const,
    details: () => [...queryKeys.news.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.news.details(), id] as const,
  },
  search: {
    all: ['search'] as const,
    results: (params: SearchParams) =>
      [...queryKeys.search.all, 'results', params] as const,
  },
  voice: {
    realtime: {
      configs: () => ['voice', 'realtime', 'configs'] as const,
    },
  },
  users: {
    wallets: {
      list: (userId?: number | null) =>
        ['users', 'wallets', userId ?? null] as const,
    },
  },
  admin: {
    me: () => ['admin', 'me'] as const,
    dashboard: {
      stats: () => ['admin', 'dashboard', 'stats'] as const,
    },
    services: {
      list: () => ['admin', 'services'] as const,
      detail: (serviceId: number | string) =>
        ['admin', 'services', 'detail', serviceId] as const,
    },
    croco: {
      gameList: (serviceId?: number | null, serviceName?: string | null) =>
        [
          'admin',
          'croco',
          'game-list',
          serviceId ?? null,
          serviceName ?? null,
        ] as const,
      winningList: (params: {
        userWalletId?: number | null
        gameId?: number | null
        days?: number | null
        limit?: number | null
        currency?: string | null
        result?: 'winning' | 'losing' | 'all' | null
      }) =>
        [
          'admin',
          'croco',
          'winning-list',
          params.userWalletId ?? null,
          params.gameId ?? null,
          params.days ?? null,
          params.limit ?? null,
          params.currency ?? null,
          params.result ?? null,
        ] as const,
    },
    wallets: {
      list: () => ['admin', 'wallets'] as const,
      detail: (walletId: number | string) =>
        ['admin', 'wallets', 'detail', walletId] as const,
      conversions: (walletId?: number | null) =>
        ['admin', 'wallets', 'conversions', walletId ?? null] as const,
    },
    rewards: {
      list: (
        walletId?: number | null,
        status?: boolean | null,
        query?: string | null
      ) =>
        [
          'admin',
          'rewards',
          walletId ?? null,
          status ?? null,
          query ?? null,
        ] as const,
      detail: (rewardId: number | string) =>
        ['admin', 'rewards', 'detail', rewardId] as const,
    },
    bonusPromotions: {
      list: (params?: { status?: boolean | null; q?: string | null }) =>
        [
          'admin',
          'bonus-promotions',
          params?.status ?? null,
          params?.q ?? null,
        ] as const,
      detail: (promotionId: number | string) =>
        ['admin', 'bonus-promotions', 'detail', promotionId] as const,
      allocations: (params?: {
        status?: string | null
        page?: number | null
        limit?: number | null
        search?: string | null
      }) =>
        [
          'admin',
          'bonus-promotions',
          'allocations',
          params?.status ?? null,
          params?.page ?? null,
          params?.limit ?? null,
          params?.search ?? null,
        ] as const,
    },
    activeBonuses: {
      list: (params?: {
        status?: string | null
        userId?: number | null
        promotionId?: number | null
      }) =>
        [
          'admin',
          'active-bonuses',
          params?.status ?? null,
          params?.userId ?? null,
          params?.promotionId ?? null,
        ] as const,
      detail: (userId: number) =>
        ['admin', 'active-bonuses', 'detail', userId] as const,
      settlementPreview: (activeBonusId: number) =>
        [
          'admin',
          'active-bonuses',
          'settlement-preview',
          activeBonusId,
        ] as const,
    },
    bonusGroupKeys: () => ['admin', 'bonus-promotions', 'group-keys'] as const,
    bonusJournals: {
      list: (params?: {
        status?: string | null
        eventType?: string | null
        promotionId?: number | null
        userId?: number | null
        page?: number | null
        limit?: number | null
        search?: string | null
      }) =>
        [
          'admin',
          'bonus-journals',
          params?.status ?? null,
          params?.eventType ?? null,
          params?.promotionId ?? null,
          params?.userId ?? null,
          params?.page ?? null,
          params?.limit ?? null,
          params?.search ?? null,
        ] as const,
      detail: (id: number | string) =>
        ['admin', 'bonus-journals', 'detail', id] as const,
    },
    bonusAgents: {
      list: () => ['admin', 'bonus-agents'] as const,
      detail: (agentId: number | string) =>
        ['admin', 'bonus-agents', 'detail', agentId] as const,
    },
    bonusAgentJournals: {
      list: (params?: {
        status?: string | null
        bonusAgentId?: number | null
        sourceUserId?: number | null
        beneficialUserId?: number | null
        page?: number | null
        limit?: number | null
        search?: string | null
      }) =>
        [
          'admin',
          'bonus-agent-journals',
          params?.status ?? null,
          params?.bonusAgentId ?? null,
          params?.sourceUserId ?? null,
          params?.beneficialUserId ?? null,
          params?.page ?? null,
          params?.limit ?? null,
          params?.search ?? null,
        ] as const,
      detail: (id: number | string) =>
        ['admin', 'bonus-agent-journals', 'detail', id] as const,
    },
    crocoTransactions: {
      list: (params?: CrocoTransactionFilters) =>
        [
          'admin',
          'croco-transactions',
          'list',
          params?.userWalletId ?? null,
          params?.walletJournalId ?? null,
          params?.userId ?? null,
          params?.gameId ?? null,
          params?.search ?? null,
          params?.dateFrom ?? null,
          params?.dateTo ?? null,
          params?.result ?? null,
          params?.minBet ?? null,
          params?.maxBet ?? null,
          params?.page ?? 1,
          params?.limit ?? 20,
        ] as const,
    },
    walletProviders: {
      list: () => ['admin', 'wallet-providers'] as const,
      detail: (providerId: number | string) =>
        ['admin', 'wallet-providers', 'detail', providerId] as const,
    },
    userWallets: {
      list: (walletId?: number | null, userId?: number | null) =>
        ['admin', 'user-wallets', walletId ?? null, userId ?? null] as const,
      detail: (recordId: number | string) =>
        ['admin', 'user-wallets', 'detail', recordId] as const,
    },
    walletJournals: {
      list: (
        walletId?: number | null,
        userId?: number | null,
        page?: number | null,
        limit?: number | null
      ) =>
        [
          'admin',
          'wallet-journals',
          walletId ?? null,
          userId ?? null,
          page ?? null,
          limit ?? null,
        ] as const,
      detail: (journalId: number | string) =>
        ['admin', 'wallet-journals', 'detail', journalId] as const,
    },
    payouts: {
      list: (params?: { status?: string | null; page?: number | null }) =>
        [
          'admin',
          'payouts',
          params?.status ?? null,
          params?.page ?? null,
        ] as const,
      detail: (payoutId: number | string) =>
        ['admin', 'payouts', 'detail', payoutId] as const,
      status: (payoutId: number | string) =>
        ['admin', 'payouts', 'status', payoutId] as const,
      balance: (currency?: string | null) =>
        ['admin', 'payouts', 'balance', currency ?? null] as const,
    },
    llm: {
      providers: () => ['admin', 'llm', 'providers'] as const,
      usage: (params?: {
        providerCode?: string | null
        limit?: number | null
        page?: number | null
        pageSize?: number | null
        start?: string | null
        end?: string | null
      }) =>
        [
          'admin',
          'llm',
          'usage',
          params?.providerCode ?? null,
          params?.limit ?? null,
          params?.page ?? null,
          params?.pageSize ?? null,
          params?.start ?? null,
          params?.end ?? null,
        ] as const,
    },
    sites: {
      list: () => ['admin', 'sites'] as const,
      current: () => ['admin', 'sites', 'current'] as const,
      detail: (siteId: number | string) =>
        ['admin', 'sites', 'detail', siteId] as const,
    },
    users: {
      list: (
        filters:
          | {
              scope?: 'admin' | 'all'
              search?: string | null
              role?: string | null
              limit?: number | null
            }
          | 'admin'
          | 'all' = 'admin'
      ) => {
        const resolvedFilters =
          typeof filters === 'string' ? { scope: filters } : filters
        return [
          'admin',
          'users',
          'list',
          resolvedFilters?.scope ?? 'admin',
          resolvedFilters?.search ?? null,
          resolvedFilters?.role ?? null,
          resolvedFilters?.limit ?? null,
        ] as const
      },
      detail: (userId: number | string) =>
        ['admin', 'users', 'detail', userId] as const,
      mobiles: (userId: number) =>
        ['admin', 'users', 'mobiles', userId] as const,
    },
    contacts: {
      list: () => ['admin', 'contacts'] as const,
      detail: (contactId: number | string) =>
        ['admin', 'contacts', 'detail', contactId] as const,
    },
    collections: {
      list: () => ['admin', 'collections'] as const,
      detail: (collectionId: number | string) =>
        ['admin', 'collections', 'detail', collectionId] as const,
    },
    s3Configs: {
      list: () => ['admin', 's3-configurations'] as const,
      detail: (configId: number | string) =>
        ['admin', 's3-configurations', 'detail', configId] as const,
      presets: () => ['admin', 's3-configurations', 'presets'] as const,
    },
    erpnextConfig: {
      list: () => ['admin', 'erpnext-config', 'list'] as const,
      detail: () => ['admin', 'erpnext-config'] as const,
    },
    agents: {
      list: () => ['admin', 'agents'] as const,
      detail: (agentId: number | string) =>
        ['admin', 'agents', 'detail', agentId] as const,
      stats: () => ['admin', 'agents', 'stats'] as const,
    },
    accessControl: {
      permissions: {
        list: (filters?: {
          code?: string | null
          roleCodes?: (string | null | undefined)[] | null
        }) =>
          [
            'admin',
            'access-control',
            'permissions',
            filters?.code?.trim() ?? null,
            filters?.roleCodes && filters.roleCodes.length > 0
              ? filters.roleCodes
                  .filter((code): code is string => Boolean(code))
                  .map(code => code.trim())
                  .filter(code => code.length > 0)
                  .sort()
                  .join(',') || null
              : null,
          ] as const,
        detail: (permissionId: number | string) =>
          [
            'admin',
            'access-control',
            'permissions',
            'detail',
            permissionId,
          ] as const,
      },
      roles: {
        list: () => ['admin', 'access-control', 'roles'] as const,
      },
    },
    flows: {
      list: () => ['admin', 'flows'] as const,
      detail: (flowId: number | string) =>
        ['admin', 'flows', 'detail', flowId] as const,
    },
    flowModules: {
      list: (siteId?: number | string | null) =>
        ['admin', 'flow-modules', siteId ?? 'current'] as const,
    },
    aiProcesses: {
      list: () => ['admin', 'ai-processes'] as const,
      detail: (processId: number | string) =>
        ['admin', 'ai-processes', 'detail', processId] as const,
    },
    teams: {
      list: () => ['admin', 'teams'] as const,
      detail: (teamId: number | string) =>
        ['admin', 'teams', 'detail', teamId] as const,
    },
    bots: {
      list: () => ['admin', 'bots'] as const,
      detail: (botId: number | string) =>
        ['admin', 'bots', 'detail', botId] as const,
    },
    aiTools: {
      list: () => ['admin', 'ai-tools'] as const,
      detail: (toolId: number | string) =>
        ['admin', 'ai-tools', 'detail', toolId] as const,
      builtInTypes: () => ['admin', 'ai-tools', 'builtin-types'] as const,
    },
    elevenLabs: {
      configs: () => ['admin', 'elevenlabs', 'configs'] as const,
    },
    voiceRealtime: {
      configs: () => ['admin', 'voice-realtime', 'configs'] as const,
    },
    embedConfigs: {
      list: (flowId?: number) => ['admin', 'embed-configs', flowId] as const,
    },
    fbWhatsApp: {
      list: () => ['admin', 'fb-whatsapp', 'configurations'] as const,
    },
    telegram: {
      list: () => ['admin', 'telegram', 'configurations'] as const,
      configs: () => ['admin', 'telegram', 'configs'] as const,
      detail: (configId: number | string) =>
        ['admin', 'telegram', 'configs', 'detail', configId] as const,
      webhook: () => ['admin', 'telegram', 'webhook'] as const,
    },
    instagram: {
      configs: () => ['admin', 'instagram', 'configs'] as const,
      detail: (configId: number | string) =>
        ['admin', 'instagram', 'configs', 'detail', configId] as const,
      webhook: () => ['admin', 'instagram', 'webhook'] as const,
    },
    sessions: {
      list: () => ['admin', 'sessions'] as const,
      detail: (sessionId: string) =>
        ['admin', 'sessions', 'detail', sessionId] as const,
    },
    reports: {
      transactions: {
        list: (params?: {
          dateFrom?: string | null
          dateTo?: string | null
          status?: string | null
          provider?: string | null
          page?: number | null
          pageSize?: number | null
        }) =>
          [
            'admin',
            'reports',
            'transactions',
            params?.dateFrom ?? null,
            params?.dateTo ?? null,
            params?.status ?? null,
            params?.provider ?? null,
            params?.page ?? null,
            params?.pageSize ?? null,
          ] as const,
        analytics: (params?: {
          dateFrom?: string | null
          dateTo?: string | null
        }) =>
          [
            'admin',
            'reports',
            'transactions',
            'analytics',
            params?.dateFrom ?? null,
            params?.dateTo ?? null,
          ] as const,
      },
      walletJournals: {
        list: (params?: {
          walletId?: number | null
          userId?: number | null
          dateFrom?: string | null
          dateTo?: string | null
          type?: string | null
          page?: number | null
          pageSize?: number | null
        }) =>
          [
            'admin',
            'reports',
            'wallet-journals',
            params?.walletId ?? null,
            params?.userId ?? null,
            params?.dateFrom ?? null,
            params?.dateTo ?? null,
            params?.type ?? null,
            params?.page ?? null,
            params?.pageSize ?? null,
          ] as const,
        summary: (params?: {
          walletId?: number | null
          dateFrom?: string | null
          dateTo?: string | null
        }) =>
          [
            'admin',
            'reports',
            'wallet-journals',
            'summary',
            params?.walletId ?? null,
            params?.dateFrom ?? null,
            params?.dateTo ?? null,
          ] as const,
      },
    },
  },
  publicBooking: {
    departments: {
      list: () => ['publicBooking', 'departments', 'list'] as const,
    },
    practitioners: {
      list: (department?: string) =>
        ['publicBooking', 'practitioners', 'list', department ?? null] as const,
    },
    services: {
      list: () => ['publicBooking', 'services', 'list'] as const,
    },
    appointments: {
      availableSlots: (
        practitionerId: string,
        date?: string,
        date_from?: string,
        date_to?: string
      ) =>
        [
          'publicBooking',
          'appointments',
          'slots',
          practitionerId,
          date ?? null,
          date_from ?? null,
          date_to ?? null,
        ] as const,
      detail: (appointmentId: string) =>
        ['publicBooking', 'appointments', 'detail', appointmentId] as const,
    },
  },
  healthcare: {
    stats: () => ['healthcare', 'stats'] as const,
    appointments: {
      recent: (limit?: number | null) =>
        ['healthcare', 'appointments', 'recent', limit ?? null] as const,
      availableSlots: (
        practitionerId: string,
        date: string,
        slotDuration: number
      ) =>
        [
          'healthcare',
          'appointments',
          'slots',
          practitionerId,
          date,
          slotDuration,
        ] as const,
    },
    practitioners: {
      list: () => ['healthcare', 'practitioners', 'list'] as const,
    },
    services: {
      list: () => ['healthcare', 'services', 'list'] as const,
    },
    patients: {
      searchByEmail: (email: string) =>
        ['healthcare', 'patients', 'search', email.toLowerCase()] as const,
    },
    meta: {
      bloodGroups: () => ['healthcare', 'meta', 'blood-groups'] as const,
      countries: () => ['healthcare', 'meta', 'countries'] as const,
    },
  },
  flowSessions: {
    detail: (sessionId: string) => ['flows', 'sessions', sessionId] as const,
  },
  agentArticles: {
    all: ['agent', 'articles'] as const,
    list: (params?: { search?: string; page?: number; pageSize?: number }) =>
      [
        ...queryKeys.agentArticles.all,
        'list',
        params?.search ?? '',
        params?.page ?? 1,
        params?.pageSize ?? 20,
      ] as const,
    detail: (articleId: number | string) =>
      [...queryKeys.agentArticles.all, 'detail', articleId] as const,
    suggestions: (query: string) =>
      [...queryKeys.agentArticles.all, 'suggestions', query] as const,
  },
  agentContacts: {
    all: ['agent', 'contacts'] as const,
    byIdentifiers: (identifiers: readonly string[]) =>
      [...queryKeys.agentContacts.all, 'match', identifiers] as const,
    byIdentifier: (identifier: string) =>
      [...queryKeys.agentContacts.all, 'detail', identifier] as const,
  },
  agentTeams: {
    all: ['agent', 'teams'] as const,
    list: () => [...queryKeys.agentTeams.all, 'list'] as const,
  },
  attachments: {
    presignedUrl: (s3Key: string) =>
      ['attachments', 'presigned-url', s3Key] as const,
  },
  thinking: {
    all: ['thinking'] as const,
    analyze: () => [...queryKeys.thinking.all, 'analyze'] as const,
    sessions: {
      all: () => [...queryKeys.thinking.all, 'sessions'] as const,
      list: (params?: {
        query?: string | null
        model?: string | null
        sessionId?: string | null
        userId?: number | null
        limit?: number | null
        offset?: number | null
      }) =>
        [
          ...queryKeys.thinking.sessions.all(),
          'list',
          params?.query ?? null,
          params?.model ?? null,
          params?.sessionId ?? null,
          params?.userId ?? null,
          params?.limit ?? null,
          params?.offset ?? null,
        ] as const,
    },
    analytics: () => [...queryKeys.thinking.all, 'analytics'] as const,
    datasets: {
      all: () => [...queryKeys.thinking.all, 'datasets'] as const,
      list: (limit?: number | null) =>
        [...queryKeys.thinking.datasets.all(), 'list', limit ?? null] as const,
      search: (params?: {
        query?: string | null
        limit?: number | null
        threshold?: number | null
      }) =>
        [
          ...queryKeys.thinking.datasets.all(),
          'search',
          params?.query ?? null,
          params?.limit ?? null,
          params?.threshold ?? null,
        ] as const,
      stats: () => [...queryKeys.thinking.datasets.all(), 'stats'] as const,
    },
    enhancePrompt: () => [...queryKeys.thinking.all, 'enhance-prompt'] as const,
  },
  playground: {
    sessions: {
      all: ['admin', 'playground', 'sessions'] as const,
      list: (filters?: Record<string, any>) =>
        ['admin', 'playground', 'sessions', 'list', filters ?? {}] as const,
      detail: (id: number | string) =>
        ['admin', 'playground', 'sessions', 'detail', id] as const,
    },
    testCases: {
      all: ['admin', 'playground', 'test-cases'] as const,
      list: (filters?: Record<string, any>) =>
        ['admin', 'playground', 'test-cases', 'list', filters ?? {}] as const,
      detail: (id: number | string) =>
        ['admin', 'playground', 'test-cases', 'detail', id] as const,
    },
    evaluations: {
      all: ['admin', 'playground', 'evaluations'] as const,
      list: (filters?: Record<string, any>) =>
        ['admin', 'playground', 'evaluations', 'list', filters ?? {}] as const,
      detail: (id: number | string) =>
        ['admin', 'playground', 'evaluations', 'detail', id] as const,
    },
  },
}
